import { useState } from "react";
import { useTranslation } from "react-i18next";
import LogoHeader from "../../components/header/logoHeader";
import toast from "react-hot-toast";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setNewUserPassword } from "../../redux/slices/loginSlice";
import Button from "../../components/common/imageButton";

/**
 * SetPassword Component
 * Allows users to set their password by entering current, new, and confirm password fields.
 */
function SetPassword() {
  const { t } = useTranslation(); // Translation hook for internationalization

  // Extract loading state from Redux store
  const { isLoading } = useSelector((state) => state.auth);

  // Local state to manage password fields, error messages, and visibility
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState("");
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /**
   * Toggle visibility of the password fields.
   *
   * @param {string} field - Field to toggle (current, new, confirm)
   */
  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  /**
   * Validate current password
   */
  const validateCurrentPassword = (value) => {
    if (!value) {
      return t("setPassword.error_empty_current_password");
    }
    return "";
  };

  /**
   * Validate new password
   */
  const validateNewPassword = (value) => {
    if (!value) {
      return t("setPassword.error_empty_new_password");
    }
    if (
      value.length < 5 ||
      !/[A-Z]/.test(value) ||
      !/[a-z]/.test(value) ||
      !/[0-9]/.test(value) ||
      !/[!@#$%^&*(),.?":{}|<>]/.test(value)
    ) {
      return t("setPassword.error_weak_password");
    }
    return "";
  };

  /**
   * Validate confirm password
   */
  const validateConfirmPassword = (value) => {
    if (!value) {
      return t("setPassword.error_empty_confirm_password");
    }
    if (value !== newPassword) {
      return t("setPassword.error_password_mismatch");
    }
    return "";
  };

  /**
   * Handle real-time validation for password fields
   */
  const handlePasswordChange = (field, value) => {
    let error = "";

    switch (field) {
      case "current":
        setCurrentPassword(value);
        error = validateCurrentPassword(value);
        break;
      case "new":
        setNewPassword(value);
        error = validateNewPassword(value);
        break;
      case "confirm":
        setConfirmPassword(value);
        error = validateConfirmPassword(value);
        break;
      default:
        break;
    }

    setLocalError(error); // Set the error if any
  };

  /**
   * Handle form submission to validate and process password change.
   */
  const handleSetPassword = async () => {
    const currentPasswordError = validateCurrentPassword(currentPassword);
    const newPasswordError = validateNewPassword(newPassword);
    const confirmPasswordError = validateConfirmPassword(confirmPassword);

    if (currentPasswordError || newPasswordError || confirmPasswordError) {
      setLocalError(
        currentPasswordError || newPasswordError || confirmPasswordError
      );
      return;
    }

    try {
      const user_id = localStorage.getItem("user_id");
      const payload = {
        user_id,
        old_password: currentPassword,
        new_password: newPassword,
      };

      // Dispatch the registerNewUser action
      const response = await dispatch(setNewUserPassword(payload)).unwrap();

      const { is_verified } = response;
      localStorage.setItem("is_verified", is_verified);

      // After registration is successful, navigate
      toast.success(t("setPassword.success_message"));
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error during setPassword:", error.message);
      if (error === "Invalid old password.") {
        toast.error(t("setPassword.old_password_incorrect"));
      }
    }
  };

  return (
    <>
      {/* Logo header for branding */}
      <LogoHeader />

      {/* Auth Section */}
      <div className="flex justify-center items-center mt-5">
        <div className="border-4 border-[#E2A32C] rounded-xl p-5 shadow-lg w-full max-w-md mx-4">
          <h2 className="text-2xl font-semibold mb-8 text-center text-white">
            {t("setPassword.title")}
          </h2>

          {/* Password Fields */}
          <div className="space-y-4">
            {/* Current Password Field */}
            <div className="relative">
              <input
                type={showPasswords.current ? "text" : "password"}
                placeholder={t("setPassword.current_password_placeholder")}
                value={currentPassword}
                onChange={(e) =>
                  handlePasswordChange("current", e.target.value)
                }
                className="w-full px-4 py-2 border  border-[#FFBF4D] rounded-lg focus:ring-2 focus:ring-[#FFBF4D] focus:outline-none text-gray-700"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => togglePasswordVisibility("current")}
              >
                {showPasswords.current ? (
                  <AiFillEyeInvisible size={20} />
                ) : (
                  <AiFillEye size={20} />
                )}
              </button>
            </div>

            {/* New Password Field */}
            <div className="relative">
              <input
                type={showPasswords.new ? "text" : "password"}
                placeholder={t("setPassword.new_password_placeholder")}
                value={newPassword}
                onChange={(e) => handlePasswordChange("new", e.target.value)}
                className="w-full px-4 py-2 border border-[#FFBF4D] rounded-lg focus:ring-2 focus:ring-[#FFBF4D] focus:outline-none text-gray-700"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => togglePasswordVisibility("new")}
              >
                {showPasswords.new ? (
                  <AiFillEyeInvisible size={20} />
                ) : (
                  <AiFillEye size={20} />
                )}
              </button>
            </div>

            {/* Confirm Password Field */}
            <div className="relative">
              <input
                type={showPasswords.confirm ? "text" : "password"}
                placeholder={t("setPassword.confirm_password_placeholder")}
                value={confirmPassword}
                onChange={(e) =>
                  handlePasswordChange("confirm", e.target.value)
                }
                className="w-full px-4 py-2 border  border-[#FFBF4D] rounded-lg focus:ring-2 focus:ring-[#FFBF4D] focus:outline-none text-gray-700"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => togglePasswordVisibility("confirm")}
              >
                {showPasswords.confirm ? (
                  <AiFillEyeInvisible size={20} />
                ) : (
                  <AiFillEye size={20} />
                )}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {localError ? (
            <div className="mt-2 ">
              <p className="text-red-500 text-sm text-center">{localError}</p>
            </div>
          ) : (
            <div className="mt-9">
              <p></p>
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-2">
            <Button
              text={isLoading ? t("loading") : t("setPassword.button_title")}
              onClick={handleSetPassword}
              loading={isLoading}
            />
          </div>
        </div>
      </div>

      {/* Decorative Image Section */}
      <div className="flex justify-center items-center mt-3">
        <img
          src="images/box.png"
          alt="Decorative Box"
          className="w-36 h-36 mt-2"
        />
      </div>
    </>
  );
}

export default SetPassword;
